import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { NO_DATA_PLACEHOLDER } from 'constants/labels';

type BathsListing = {
  bathsCount?: number | null;
  bathsFull?: number | null;
  bathsHalf?: number | null;
};

const SIDE_GAP = 70;

// Only one breakdown is open at a time across the page.
let closeOpenBreakdown: (() => void) | null = null;

// "5 full · 1 half"; null when there is nothing to explain.
export function getBathsBreakdown({ bathsFull, bathsHalf }: BathsListing) {
  if (!bathsHalf) return null;
  if (!bathsFull) return `${bathsHalf} half`;

  return `${bathsFull} full · ${bathsHalf} half`;
}

// Bath count with the breakdown on hover (tap on touch screens).
const BathsCount: React.FC<{
  listing: BathsListing;
  fallback?: string | number;
}> = ({ listing, fallback = NO_DATA_PLACEHOLDER }) => {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const pointerTypeRef = useRef('mouse');
  const breakdown = getBathsBreakdown(listing);
  const value = listing.bathsCount || fallback;

  useEffect(() => {
    if (!rect) return;

    const close = () => setRect(null);
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!triggerRef.current?.contains(event.target as Node)) close();
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    closeOpenBreakdown?.();
    closeOpenBreakdown = close;
    document.addEventListener('click', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    window.addEventListener('scroll', close, true);

    return () => {
      if (closeOpenBreakdown === close) closeOpenBreakdown = null;
      document.removeEventListener('click', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
      window.removeEventListener('scroll', close, true);
    };
  }, [rect]);

  if (!breakdown) return <>{value}</>;

  const open = () => setRect(triggerRef.current!.getBoundingClientRect());
  const close = () => setRect(null);
  const onHover = (show: boolean) => (event: React.PointerEvent) => {
    if (event.pointerType === 'mouse') (show ? open : close)();
  };

  const handleTap = (event: React.MouseEvent) => {
    if (pointerTypeRef.current === 'mouse') return;
    // The count often sits inside a link: a tap shows the breakdown instead.
    event.preventDefault();
    event.stopPropagation();
    if (rect) close();
    else open();
  };

  // Opens above the count, or below it when it sits at the top of the screen.
  const below = !!rect && rect.top < 40;

  return (
    <>
      <span
        ref={triggerRef}
        className="border-b border-dotted border-current cursor-help"
        onPointerEnter={onHover(true)}
        onPointerLeave={onHover(false)}
        onPointerDown={(event) => {
          pointerTypeRef.current = event.pointerType;
        }}
        onClick={handleTap}
      >
        {value}
        <span className="sr-only"> ({breakdown})</span>
      </span>
      {rect &&
        createPortal(
          <span
            aria-hidden="true"
            style={{
              top: below ? rect.bottom + 6 : rect.top - 6,
              left: Math.min(
                Math.max(rect.left + rect.width / 2, SIDE_GAP),
                window.innerWidth - SIDE_GAP
              ),
            }}
            className={`fixed z-[1400] px-3 py-1 transform -translate-x-1/2 ${
              below ? '' : '-translate-y-full'
            } whitespace-nowrap normal-case pointer-events-none text-14-26-0.3 font-sohneBuch text-black bg-gold-background border border-beige-dark`}
          >
            {breakdown}
          </span>,
          document.body
        )}
    </>
  );
};

export default BathsCount;
